import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import "./SalesReport.css";
import { getDailySalesReport } from "../../api/reportsAPI";
import Inputs from "../../components/inputs/Inputs";

const INITIAL_FROM_DATE = moment().subtract(0, "days").format("YYYY-MM-DD");
const INITIAL_TO_DATE = moment().format("YYYY-MM-DD");

const SalesReport = () => {
  const [salesReportByDate, setSalesReportByDate] = useState([]);
  const [expandedDates, setExpandedDates] = useState({
    [INITIAL_FROM_DATE]: true,
  });
  const [formValues, setFormValues] = useState({
    fromDate: INITIAL_FROM_DATE,
    toDate: INITIAL_TO_DATE,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState("");

  const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const formatCurrency = (value) => {
    const amount = safeNumber(value);

    return amount.toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateValue) => {
    const formatted = moment(dateValue, "YYYY-MM-DD", true);

    if (!formatted.isValid()) {
      return "-";
    }

    return formatted.format("DD MMM YYYY");
  };

  const normalizeDateValue = (value) => {
    // Supports custom Input components that return:
    // 1. "2026-09-23"
    // 2. Date object
    // 3. an event object
    const rawValue = value?.target?.value ?? value;

    if (!rawValue) {
      return "";
    }

    const parsedDate = moment(rawValue);

    return parsedDate.isValid()
      ? parsedDate.format("YYYY-MM-DD")
      : "";
  };

  const validateDates = (values) => {
    const nextErrors = {};

    if (!values.fromDate) {
      nextErrors.fromDate = ["Start date is required"];
    } else if (!moment(values.fromDate, "YYYY-MM-DD", true).isValid()) {
      nextErrors.fromDate = ["Enter a valid start date"];
    }

    if (!values.toDate) {
      nextErrors.toDate = ["End date is required"];
    } else if (!moment(values.toDate, "YYYY-MM-DD", true).isValid()) {
      nextErrors.toDate = ["Enter a valid end date"];
    }

    if (
      values.fromDate &&
      values.toDate &&
      moment(values.fromDate).isAfter(moment(values.toDate))
    ) {
      nextErrors.toDate = ["End date cannot be earlier than start date"];
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const loadSalesReport = useCallback(
    async (dates = formValues) => {
      if (!validateDates(dates)) {
        return;
      }

      setIsLoading(true);
      setRequestError("");

      try {
        const response = await getDailySalesReport({
          startDate: dates.fromDate,
          endDate: dates.toDate,
        });

        if (response?.status !== 200) {
          throw new Error(
            response?.data?.message ||
              "Could not load the daily sales report"
          );
        }

        // Supports common API response shapes:
        // { data: { data: [...] } }
        // { data: [...] }
        const reportData = response?.data?.data ?? response?.data ?? [];

        if (!Array.isArray(reportData)) {
          throw new Error("The sales report response is not a valid array");
        }

        const normalizedReport = reportData.map((day) => ({
          ...day,
          date: day?.date ?? "",
          totalSellAmount: safeNumber(day?.totalSellAmount),
          totalProfit: safeNumber(day?.totalProfit),
          totalExpense: safeNumber(day?.totalExpense),
          netProfit: safeNumber(day?.netProfit),
          items: Array.isArray(day?.items) ? day.items : [],
        }));

        setSalesReportByDate(normalizedReport);

        // Expand the first returned day after a successful search.
        if (normalizedReport.length > 0) {
          setExpandedDates({
            [normalizedReport[0].date]: true,
          });
        } else {
          setExpandedDates({});
        }
      } catch (error) {
        console.error("Failed to load sales report:", error);

        setSalesReportByDate([]);
        setExpandedDates({});
        setRequestError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load the sales report. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [formValues]
  );

  // Load report once when component mounts.
  useEffect(() => {
    loadSalesReport({
      fromDate: INITIAL_FROM_DATE,
      toDate: INITIAL_TO_DATE,
    });
  }, []); // Intentional: initial page load only.

  // Derived data should use useMemo; do not calculate it in useEffect.
  const grandTotals = useMemo(() => {
    return salesReportByDate.reduce(
      (totals, day) => {
        const items = Array.isArray(day?.items) ? day.items : [];

        const quantityForDay = items.reduce(
          (itemTotal, item) => itemTotal + safeNumber(item?.quantity),
          0
        );

        return {
          totalSellAmount:
            totals.totalSellAmount + safeNumber(day?.totalSellAmount),
          totalProfit: totals.totalProfit + safeNumber(day?.totalProfit),
          totalExpense: totals.totalExpense + safeNumber(day?.totalExpense),
          netProfit: totals.netProfit + safeNumber(day?.netProfit),
          totalQuantity: totals.totalQuantity + quantityForDay,
        };
      },
      {
        totalSellAmount: 0,
        totalProfit: 0,
        totalExpense: 0,
        netProfit: 0,
        totalQuantity: 0,
      }
    );
  }, [salesReportByDate]);

  const handleChange = (name, value) => {
    const normalizedValue = normalizeDateValue(value);

    setFormValues((previous) => ({
      ...previous,
      [name]: normalizedValue,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: undefined,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    loadSalesReport(formValues);
  };

  const toggleDate = (date) => {
    if (!date) return;

    setExpandedDates((previous) => ({
      ...previous,
      [date]: !previous[date],
    }));
  };

  return (
    <section className="sales-report">
      <form className="sales-report__filters" onSubmit={handleSubmit}>
  <div className="sales-report__filter-row">
    <div className="sales-report__filter-field">
      <Inputs
        name="fromDate"
        size="small"
        label="Start Date"
        value={formValues.fromDate}
        inputType="date"
        onChange={(value) => handleChange("fromDate", value)}
        error={Boolean(errors.fromDate)}
        helperText={errors.fromDate?.[0] ?? ""}
      />
    </div>

    <div className="sales-report__filter-field">
      <Inputs
        name="toDate"
        size="small"
        label="End Date"
        value={formValues.toDate}
        inputType="date"
        maxDate={new Date()}
        onChange={(value) => handleChange("toDate", value)}
        error={Boolean(errors.toDate)}
        helperText={errors.toDate?.[0] ?? ""}
      />
    </div>

    <div className="sales-report__filter-action">
      <button
        type="submit"
        className="sales-report__fetch-button"
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : "Fetch Data"}
      </button>
    </div>
  </div>
</form>

      <header className="sales-report__header">
        <div>
          <p className="sales-report__eyebrow">Business performance</p>
          <h1 className="sales-report__title">Daily Sales Report</h1>
          <p className="sales-report__subtitle">
            Review daily sales, expenses, profit, and item-level performance.
          </p>
        </div>

        <div className="sales-report__period">
          <span>Report period</span>
          <strong>
            {formatDate(formValues.fromDate)} – {formatDate(formValues.toDate)}
          </strong>
        </div>
      </header>

      {requestError && (
        <div className="sales-report__error" role="alert">
          {requestError}
        </div>
      )}

      <div className="sales-report__summary-grid">
        <article className="summary-card summary-card--sales">
          <span className="summary-card__label">Total Sales</span>
          <strong className="summary-card__value">
            {formatCurrency(grandTotals.totalSellAmount)}
          </strong>
          <span className="summary-card__hint">
            Sales value for this report
          </span>
        </article>

        <article className="summary-card summary-card--profit">
          <span className="summary-card__label">Total Profit</span>
          <strong className="summary-card__value">
            {formatCurrency(grandTotals.totalProfit)}
          </strong>
          <span className="summary-card__hint">Before expenses</span>
        </article>

        <article className="summary-card summary-card--expense">
          <span className="summary-card__label">Total Expenses</span>
          <strong className="summary-card__value">
            {formatCurrency(grandTotals.totalExpense)}
          </strong>
          <span className="summary-card__hint">
            Recorded operating expenses
          </span>
        </article>

        <article
          className={`summary-card ${
            grandTotals.netProfit >= 0
              ? "summary-card--positive"
              : "summary-card--negative"
          }`}
        >
          <span className="summary-card__label">Net Profit</span>
          <strong className="summary-card__value">
            {formatCurrency(grandTotals.netProfit)}
          </strong>
          <span className="summary-card__hint">
            {grandTotals.netProfit >= 0
              ? "Net positive result"
              : "Net loss recorded"}
          </span>
        </article>

        <article className="summary-card summary-card--quantity">
          <span className="summary-card__label">Total Quantity Sold</span>
          <strong className="summary-card__value">
            {formatCurrency(grandTotals.totalQuantity)}
          </strong>
          <span className="summary-card__hint">
            Items sold across all dates
          </span>
        </article>
      </div>

      {isLoading && (
        <div className="sales-report__loading">
          Loading daily sales report...
        </div>
      )}

      {!isLoading && !requestError && salesReportByDate.length === 0 && (
        <div className="sales-report__empty">
          No sales data was found for the selected date range.
        </div>
      )}

      <div className="sales-report__daily-list">
        {salesReportByDate.map((day, dayIndex) => {
          const items = Array.isArray(day?.items) ? day.items : [];
          const isExpanded = Boolean(expandedDates[day?.date]);

          const totalQuantity = items.reduce(
            (total, item) => total + safeNumber(item?.quantity),
            0
          );

          return (
            <article
              className="daily-report-card"
              key={day?.date || `sales-day-${dayIndex}`}
            >
              <button
                type="button"
                className="daily-report-card__header"
                onClick={() => toggleDate(day?.date)}
                aria-expanded={isExpanded}
              >
                <div className="daily-report-card__date">
                  <span className="daily-report-card__date-label">
                    Sales date
                  </span>

                  <strong>{formatDate(day?.date)}</strong>

                  <span className="daily-report-card__items-count">
                    {items.length} product types ·{" "}
                    {formatCurrency(totalQuantity)} units
                  </span>
                </div>

                <div className="daily-report-card__quick-totals">
                  <div>
                    <span>Sales</span>
                    <strong>{formatCurrency(day?.totalSellAmount)}</strong>
                  </div>

                  <div>
                    <span>Net profit</span>
                    <strong
                      className={
                        safeNumber(day?.netProfit) >= 0
                          ? "amount--positive"
                          : "amount--negative"
                      }
                    >
                      {formatCurrency(day?.netProfit)}
                    </strong>
                  </div>

                  <span className="daily-report-card__toggle">
                    {isExpanded ? "Hide details ▲" : "View details ▼"}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="daily-report-card__content">
                  <div className="daily-metrics">
                    <div className="daily-metric">
                      <span>Total sales</span>
                      <strong>
                        {formatCurrency(day?.totalSellAmount)}
                      </strong>
                    </div>

                    <div className="daily-metric">
                      <span>Gross profit</span>
                      <strong>{formatCurrency(day?.totalProfit)}</strong>
                    </div>

                    <div className="daily-metric">
                      <span>Total expense</span>
                      <strong>{formatCurrency(day?.totalExpense)}</strong>
                    </div>

                    <div className="daily-metric">
                      <span>Net profit</span>
                      <strong
                        className={
                          safeNumber(day?.netProfit) >= 0
                            ? "amount--positive"
                            : "amount--negative"
                        }
                      >
                        {formatCurrency(day?.netProfit)}
                      </strong>
                    </div>
                  </div>

                  <div className="sales-report__table-wrapper">
                    <table className="sales-report__table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="text-right">Quantity</th>
                          <th className="text-right">Selling Price</th>
                          <th className="text-right">Profit</th>
                        </tr>
                      </thead>

                      <tbody>
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="sales-report__no-items">
                              No item sales found for this date.
                            </td>
                          </tr>
                        ) : (
                          items.map((sale, index) => (
                            <tr
                              key={
                                sale?.itemCode ||
                                `${day?.date}-${sale?.item}-${index}`
                              }
                            >
                              <td>{sale?.item ?? "-"}</td>
                              <td className="text-right">
                                {formatCurrency(sale?.quantity)}
                              </td>
                              <td className="text-right">
                                {formatCurrency(sale?.sellingPrice)}
                              </td>
                              <td className="text-right amount--positive">
                                {formatCurrency(sale?.profit)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>

                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td className="text-right">
                            {formatCurrency(totalQuantity)}
                          </td>
                          <td className="text-right">
                            {formatCurrency(day?.totalSellAmount)}
                          </td>
                          <td className="text-right amount--positive">
                            {formatCurrency(day?.totalProfit)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default SalesReport;